import { PropsWithChildren, ReactNode } from 'react';
import { Check } from '@mui/icons-material';
import { Box, BoxProps, Card, IconButton, Tooltip, Typography } from '@mui/material';
import { Documents, Notes, ToolbarContainer, dataModelIcons } from '/src/components';
import { DesktopLayout } from '/src/layouts';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { themeColors } from '/src/theme';

export type FormPageProps = PropsWithChildren & {
  /**
   * Data model to perform CRUD on
   */
  modelType: DataModelType;
  /**
   * Data model display name
   */
  modelName?: string;
  /**
   * CRUD operation to perform
   */
  mode: CRUDMode;
  /**
   * Submits form data
   * @returns void | Promise<void>
   */
  onSubmit: () => void | Promise<void>;
  /**
   * Verbiage to use when talking about the selected CRUD action
   */
  verb: string;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Display icon representing the given model type
   */
  icon?: ReactNode;
  /**
   * Page title
   */
  title?: string;
  /**
   * Label for page's submit button
   */
  submitLabel?: string;
  /**
   * Optional sidebar component to display
   */
  sidebar?: ReactNode;
  /**
   * Display sidebar items
   */
  sidebarItems?: boolean[];
  /**
   * Flex value for sidebar
   */
  sidebarFlex?: string;
  /**
   * Flex value for page content
   */
  contentFlex?: string;
  /**
   * Page element width
   */
  width?: BoxProps['width'];
  /**
   * Toggles which sidebar items are open
   * @param items Sidebar items
   * @returns void
   */
  setSidebarItems?: (items: boolean[]) => void;
  /**
   * Config options for the Notes component
   */
  noteOptions?: {
    notes: Note[];
    onChange: (notes: Note[]) => void;
    title?: string;
    tooltip?: string;
  };
  /**
   * Config options for the Documents component
   */
  documentOptions?: {
    files: string[];
    onChange: (files: string[]) => void;
    title?: string;
    tooltip?: string;
  };
  /**
   * Flex value for Notes and Documents components
   */
  notesDocsFlex?: string;
};

/**
 * Form Page Layout
 */
export const FormPage: React.FC<FormPageProps> = ({
  children,
  modelType,
  modelName,
  mode,
  onSubmit,
  verb,
  id,
  submitLabel,
  icon,
  title,
  sidebar,
  sidebarItems,
  sidebarFlex,
  contentFlex,
  setSidebarItems,
  noteOptions,
  documentOptions,
  notesDocsFlex,
  width,
}) => {
  /** Computed **/
  const pageId = id || `${modelType.toLowerCase()}Form`;
  const modelIcon = icon || dataModelIcons[modelType];
  const pageTitle = title || modelName || `${verb} ${modelType}`;
  const showSidebar = sidebar || (sidebarItems && setSidebarItems && (noteOptions || documentOptions));

  /** Render **/
  const renderNotes = (index = 0) =>
    sidebarItems &&
    setSidebarItems &&
    noteOptions && (
      <Notes
        mode={mode}
        notes={noteOptions.notes ?? []}
        sidebarIndex={index}
        sidebarItems={sidebarItems}
        setSidebarItems={setSidebarItems}
        updateNotes={noteOptions.onChange}
        title={noteOptions.title}
        tooltip={noteOptions.tooltip}
      />
    );

  const renderDocuments = (index = 1) =>
    sidebarItems &&
    setSidebarItems &&
    documentOptions && (
      <Documents
        mode={mode}
        documents={documentOptions.files ?? []}
        sidebarIndex={index}
        sidebarItems={sidebarItems}
        setSidebarItems={setSidebarItems}
        updateDocuments={documentOptions.onChange}
        title={documentOptions.title}
        tooltip={documentOptions.tooltip}
      />
    );

  return (
    <DesktopLayout>
      <form noValidate name={pageId} id={pageId} onSubmit={onSubmit}>
        {/* <Box display="flex" className="md-content" overflow="hidden"> */}
        <Box component="md-content" className="md-default-theme" overflow="hidden">
          <Box layout-gt-sm="row">
            {showSidebar ? (
              <Box flex={sidebarFlex}>
                {sidebar}
                {sidebarItems && setSidebarItems && (noteOptions || documentOptions) && (
                  <Box flex={notesDocsFlex}>
                    {noteOptions && renderNotes()}
                    {documentOptions && renderDocuments()}
                  </Box>
                )}
              </Box>
            ) : (
              <Box display="flex" flex={1} />
            )}

            <Box sx={!width && contentFlex ? { flex: contentFlex } : { width: width || '100%' }}>
              {/* DETAILS */}
              <Card component="md-card" style={{ background: 'white' }}>
                <ToolbarContainer>
                  <Box component="span" justifyContent="start" alignItems="center" display="flex" flex={1} />

                  <Box component="span" justifyContent="center" alignItems="center" display="flex" gap="6px">
                    {modelIcon && (
                      <Tooltip title={pageTitle}>
                        <>{modelIcon}</>
                      </Tooltip>
                    )}
                    <Typography fontSize="34px" lineHeight="40px" fontWeight={100}>
                      {pageTitle}
                    </Typography>
                  </Box>

                  <Box component="span" justifyContent="end" alignItems="center" display="flex" flex={1}>
                    <Tooltip title={submitLabel || pageTitle}>
                      <IconButton
                        sx={{ background: themeColors.accent }}
                        aria-label={submitLabel || pageTitle}
                        onClick={onSubmit}
                      >
                        <Check />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ToolbarContainer>

                <Box flexDirection="column" padding="0 16px 0 16px" display="flex">
                  {children}
                </Box>
              </Card>
            </Box>

            <Box display="flex" flex={1} />
          </Box>
        </Box>
      </form>
    </DesktopLayout>
  );
};
